import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, HelpCircle, ChevronDown, ArrowRight, MessageCircle, Clock, Shield, Zap } from 'lucide-react';
import SEOHead from '@/components/SEOHead';
import Navigation from '@/components/Navigation';
import AboutSection from '@/components/AboutSection';
import Footer from '@/components/Footer';
import CookieConsent from '@/components/CookieConsent';
import BackToTop from '@/components/BackToTop';
import { usePricing } from '@/context/PricingContext';

const FAQS = [
  {
    question: 'How long does it take to build a website?',
    answer: 'It depends on the size and scope of the project. A straightforward 5-page site could be ready in just a few days, while a larger project with custom features might take a few weeks. It also depends on how busy things are on my end. I will always give you a realistic timeline upfront so there are no surprises.',
  },
  {
    question: 'What do I need to get started?',
    answer: 'Just an idea of what your business does and what you want from your website. I will guide you through everything else. If you have a logo, brand colours, or content ready, that is a bonus — but not essential. I can help with all of that.',
  },
  {
    question: 'Will my website work on mobile?',
    answer: 'Absolutely. Every website I build is fully responsive and tested across all devices — phones, tablets, and desktops. Over 60% of web traffic comes from mobile, so this is non-negotiable.',
  },
  {
    question: 'Do I need to write my own content?',
    answer: 'It helps if you can provide some basic information about your business, but I can guide you on what to write and help shape the messaging. If you need full copywriting, we can discuss that as part of your project.',
  },
  {
    question: 'What happens after the website goes live?',
    answer: 'I do not disappear after launch. I am happy to support you for as long as you need. I also offer a monthly maintenance package for ongoing updates, security, backups, and small changes — so you can focus on running your business while I keep your site in top shape.',
  },
  {
    question: 'Can you work with my existing website?',
    answer: 'To deliver the best possible result, I prefer to build from the ground up using our own in-house tools and workflow. This means your site is built on a clean, modern foundation with no legacy issues holding it back. If you have an existing site, I will carry over your best content and make sure nothing gets lost in the process.',
  },
  {
    question: 'How much does a website cost?',
    answer: 'Websites start from £399 for a simple single-page site. Most small business sites fall in the £800–£3,800 range depending on the number of pages, features, and complexity. I will always give you a clear quote before any work starts — no hidden fees.',
  },
  {
    question: 'Do you offer ongoing maintenance?',
    answer: 'Yes. I offer monthly care plans that cover updates, security monitoring, backups, and small content changes. It means your site stays fast, secure, and up to date without you having to worry about a thing.',
  },
  {
    question: 'What is the admin panel?',
    answer: 'Every site I build comes with a custom admin panel — a simple dashboard where you can update your own text, swap images, and make changes to your website in real time. No need to email me and wait days for a small tweak. It is included free with all care plans, and it is designed for people who are not technical.',
  },
];

export default function FaqPage() {
  const [openIndex, setOpenIndex] = useState(null);
  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);
  const pricing = usePricing();
  const llPrice = pricing.local_launch?.price || '399';

  const faqs = FAQS.map(f =>
    f.question === 'How much does a website cost?'
      ? { ...f, answer: `Websites start from £${llPrice} for a simple single-page site. Most small business sites fall in the £800–£3,800 range depending on the number of pages, features, and complexity. I will always give you a clear quote before any work starts — no hidden fees.` }
      : f
  );

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] transition-colors duration-300">
      <SEOHead
        title="FAQ & About | Web Design Bournemouth | UI Designs"
        description="Common questions about web design for Bournemouth businesses. Learn about pricing, timelines, and what working with UI Designs looks like."
        canonical="https://uidesigns.co.uk/faq"
        keywords="web design FAQ, website questions Bournemouth, about UI Designs, web designer Bournemouth"
      />
      <Navigation />

      {/* Dark header */}
      <div className="relative bg-[#0A0A0A] pt-32 pb-16 md:pt-36 md:pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-[#00E5FF]/5 rounded-full blur-[120px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#00E5FF] transition-colors mb-8" data-testid="faq-back-link">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-[#00E5FF]/10 border border-[#00E5FF]/20 flex items-center justify-center">
              <HelpCircle className="w-6 h-6 text-[#00E5FF]" />
            </div>
            <h1 data-testid="faq-page-title" className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">
              Frequently Asked Questions
            </h1>
          </div>
          <p className="text-base sm:text-lg text-slate-400 max-w-xl mt-3">
            Everything you need to know before getting started. Can't find your answer? Just get in touch.
          </p>
        </div>
      </div>

      <main>
        {/* Quick info strip */}
        <div className="bg-[#F8F9FA] dark:bg-[#0D0D0D] border-b border-slate-200 dark:border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
              {[
                { icon: Clock, label: '24hr Response', desc: 'Quick replies' },
                { icon: Shield, label: 'No Hidden Fees', desc: 'Clear pricing' },
                { icon: Zap, label: 'Fast Turnaround', desc: 'Days, not months' },
                { icon: MessageCircle, label: 'Free Consultation', desc: 'No obligation' },
              ].map(({ icon: Icon, label, desc }) => (
                <div key={label} className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#00E5FF]/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-[#00E5FF]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#0A0A0A] dark:text-white leading-tight">{label}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ section */}
        <section id="faq" data-testid="faq-section" className="py-16 md:py-24">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div
                  key={i}
                  data-testid={`faq-item-${i}`}
                  className="bg-white dark:bg-[#111111] rounded-2xl border border-slate-200/80 dark:border-white/10 overflow-hidden transition-all duration-300 hover:border-slate-300 dark:hover:border-white/20"
                >
                  <button
                    data-testid={`faq-toggle-${i}`}
                    onClick={() => toggle(i)}
                    className="w-full flex items-center justify-between px-6 py-5 text-left"
                  >
                    <span className="font-['Outfit'] text-base sm:text-lg font-semibold text-[#0A0A0A] dark:text-white pr-4">
                      {faq.question}
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-300 ${
                        openIndex === i ? 'rotate-180 text-[#00E5FF]' : ''
                      }`}
                    />
                  </button>
                  <div className={`transition-all duration-300 ease-in-out ${openIndex === i ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                    <p className="px-6 pb-5 text-sm sm:text-base leading-relaxed text-slate-600 dark:text-slate-400">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA card */}
            <div className="mt-12 bg-[#0A0A0A] rounded-2xl p-8 sm:p-10 relative overflow-hidden" data-testid="faq-cta-card">
              <div className="absolute top-0 right-0 w-48 h-48 bg-[#00E5FF]/10 rounded-full blur-[80px]" />
              <div className="relative">
                <h3 className="font-['Outfit'] text-xl sm:text-2xl font-bold text-white mb-2">Still have a question?</h3>
                <p className="text-sm sm:text-base text-slate-400 mb-6 max-w-md">
                  Drop me an email or send a text — I'm happy to chat through your project with no obligation.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/contact"
                    data-testid="faq-contact-btn"
                    className="inline-flex items-center justify-center gap-2 bg-[#00E5FF] text-[#0A0A0A] px-6 py-3 rounded-full text-sm font-semibold hover:bg-white transition-colors"
                  >
                    Get in Touch
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <a
                    href="mailto:info@uidesigns.co.uk"
                    className="inline-flex items-center justify-center gap-2 border border-white/10 text-slate-300 px-6 py-3 rounded-full text-sm font-medium hover:border-white/30 hover:text-white transition-colors"
                  >
                    info@uidesigns.co.uk
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <AboutSection />
      </main>

      <Footer />
      <CookieConsent />
      <BackToTop />
    </div>
  );
}
